import { useEffect, useRef } from 'react';
import axios from 'axios';
import { notificationService } from '../utils/notificationService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Hook to check for upcoming task reminders and show browser notifications
 * Only active when user has selected "notification" as reminder method
 */
export const useTaskReminders = (user) => {
  const shownRemindersRef = useRef(new Set()); // Track already shown reminders
  const pollingIntervalRef = useRef(null);

  useEffect(() => {
    // Only activate if user exists and has notification method selected
    if (!user || user.task_reminder_method !== 'notification') {
      console.log('📴 Task reminder polling disabled:', user?.task_reminder_method);
      return;
    }

    // Check if notifications are supported and enabled
    if (!notificationService.isSupported()) {
      console.warn('⚠️ Browser notifications not supported');
      return;
    }

    if (!notificationService.isEnabled()) {
      console.warn('⚠️ Browser notifications not enabled');
      return;
    }

    console.log('✅ Task reminder polling activated');

    // Function to check for upcoming reminders
    const checkReminders = async () => {
      try {
        const response = await axios.get(`${API}/reminders/upcoming`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.data.success && response.data.reminders.length > 0) {
          console.log('🔔 Found upcoming reminders:', response.data.reminders);

          for (const reminder of response.data.reminders) {
            // Check if we already showed this reminder
            const reminderKey = `${reminder.id}-${reminder.reminder_time}`;
            
            if (!shownRemindersRef.current.has(reminderKey)) {
              console.log(`🔔 Showing notification for task: ${reminder.title}`);
              
              // Show notification
              const notification = await notificationService.showNotification(
                '⏰ Task Reminder - Due in 1 Hour',
                {
                  body: `${reminder.title}\nDue at: ${reminder.time}`,
                  icon: '/logo192.png',
                  tag: `task-reminder-${reminder.id}`,
                  data: { taskId: reminder.id }
                }
              );

              if (notification) {
                // Handle notification click
                notification.onclick = () => {
                  window.focus();
                  notification.close();
                  // Could navigate to task details here
                  console.log('User clicked notification for task:', reminder.id);
                };

                // Mark as shown
                shownRemindersRef.current.add(reminderKey);
                
                // Clean up old entries after 2 hours
                setTimeout(() => {
                  shownRemindersRef.current.delete(reminderKey);
                }, 2 * 60 * 60 * 1000);
              }
            }
          }
        }
      } catch (error) {
        if (error.response?.status !== 401) {
          console.error('Error checking reminders:', error);
        }
      }
    };

    // Check immediately
    checkReminders();

    // Poll every 2 minutes
    pollingIntervalRef.current = setInterval(checkReminders, 2 * 60 * 1000);

    // Cleanup
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        console.log('🛑 Task reminder polling stopped');
      }
    };
  }, [user]);

  return null;
};
